/**
 * Unified API Client - Resource-Oriented Architecture
 *
 * Provides a clean, predictable interface for all platform resources
 * following first-principles design with:
 * - Clean resource paths without /api/v1 prefix (gateway handles routing)
 * - CRUD operations for all resources
 * - Sub-resource methods (metrics, state, etc.)
 * - Real-time WebSocket support
 * - Consistent error handling
 * - TypeScript type safety
 */

import ky, { HTTPError, type AfterResponseState, type KyInstance, type BeforeRequestState, type BeforeErrorState } from 'ky'
import type { RequestOptions } from '@/api/client'
import { handleUnauthorized } from '@agion/auth'
import { isApiFailure } from '@agion/ui/api-failure'
import { missionWritePermissionPending } from './mission-write-pending'
import { classificationWriteFailureOf } from '../services/mission-classification.service'
import { requestMissionDeletion, requestMissionAgentRemoval } from './mission-removal'
import { config } from '../shared/config'
import { adapterRetryUnlessRefused, retryAfterAnswer } from '../shared/api/query-policies'
import { createLogger } from '../shared/lib/logger'
import { keysToCamel, keysToSnake } from '../shared/lib/utils'
import { UnifiedApiError, adapterFetch, adapterError } from './api-error'
import type { OrganizationAuditOptions, OrganizationAuditPage } from '../services/organizationService'
import type { AgentDiscoveryResponse } from '../types/api/responses'
import type {
  Agent,
  AgentWithDetails,
  AgentRegistrationRequest,
  AgentDeploymentRequest,
  AgentTask,
  AgentMetrics,
  RuntimeHost,
  AgentListResponse,
  AgentMissionAssignment,
  AgentRunMetrics,
  AgentStatistics,
  GovernanceReview,
  AgentLifecycleState,
  AgentStatus,
  AgentFramework,
  TrustTier,
  A2AMessage
} from '../types/agent.types'
import type {
  Mission,
  MissionState,
  CreateMissionRequest,
  UpdateMissionRequest,
  MissionParticipant
} from '../types/mission.types'
import {
  parseExportCoherenceHeaders,
  type ExportMissionResult,
} from './mission-export-coherence'

const logger = createLogger('UnifiedAPIClient')

// Bounded retry policy for idempotent GETs. `limit`/`statusCodes` cover transient
// backend hiccups; `backoffLimit` caps the exponential backoff between attempts
// so a transient stall cannot compound into a multi-minute blank state — e.g. the
// providers page during a governance pool-starvation burst (objective O7). The
// backend additionally fast-fails pool starvation (pgx AcquireTimeout), so each
// attempt now returns in ~seconds rather than hanging to `apiTimeout`.
export const API_RETRY_CONFIG = {
  limit: config.performance.retryAttempts,
  methods: ['get'],
  statusCodes: [408, 413, 429, 500, 502, 503, 504],
  // Cap inter-attempt backoff (ms). Without this, ky's exponential backoff can
  // add long sleeps on top of the per-attempt timeout × retries.
  backoffLimit: 2000,
  // afterResponse throws UnifiedApiError, not ky's HTTPError, so statusCodes
  // above never applies to an answered call; a 4xx other than 408 and 429 is
  // not asked again.
  shouldRetry: adapterRetryUnlessRefused,
}

// Per-call escape hatch from the client defaults above, for a request whose
// upstream is not the platform itself. The mission runtime-package listing, for
// one, is proxied to the customer's Edge host: the retry policy turns a stalled
// host into a multi-minute wait for a read the UI can degrade instead
// (Agion#2005). Omitting the options leaves every default in place, so no other
// caller changes behaviour.
// The shape is the other API client's RequestOptions (src/api/client.ts), so
// the two clients take the same per-call options under the same names.
export type ApiRequestOptions = Pick<RequestOptions, 'retry' | 'timeout' | 'signal'> & {
  /** Request headers added to the client's own (for example X-Agion-Open-Reason). */
  headers?: Record<string, string>
}

function requestOverrides(options?: ApiRequestOptions): ApiRequestOptions {
  if (!options) return {}
  return {
    ...(options.retry !== undefined ? { retry: options.retry } : {}),
    ...(options.timeout !== undefined ? { timeout: options.timeout } : {}),
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
    ...(options.headers !== undefined ? { headers: options.headers } : {}),
  }
}

// ============= Core Types =============

// The adapter failure lives in ./api-error (ADR-20260925); re-exported here
// for the callers that import it with the client.
export { UnifiedApiError }

export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
  meta?: Record<string, unknown>
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  meta?: Record<string, unknown>
}

export interface WebSocketMessage<T = unknown> {
  type: string
  resource: string
  resourceId: string
  data: T
  timestamp: string
}

// ADR-051 slice 3/9: scoped node-patch wire types.
export interface PatchMissionNodeRequest {
  classificationConfirmation?: string
  node: unknown // a dag.Node-shaped object (the node's new content)
  source: 'human' | 'ai'
  baseDocVersion: number // becomes base_doc_version on the wire
}

export interface PatchMissionNodeResponse {
  missionId: string
  nodeId: string
  applied: boolean
  conflict: boolean
  conflictNodeId?: string
  docVersion: number
  versionNumber: number
  // Transient backend contention (HTTP 503): the missions row was locked by a
  // concurrently-running mission and the scoped patch fast-failed instead of
  // blocking. The edit was NOT rejected — it is held locally and persists on the
  // next save/retry. Distinct from `conflict` (409, stale base version).
  transient?: boolean
  // The server stored the edit but could not confirm access for it yet
  // (agion/Agion#2677): applied, with the versions the server stored.
  permissionPending?: boolean
  // The document version a 409 names as current (ADR-20260929 D8).
  currentDocVersion?: number
}

// ADR-20260621 AA1: semantic dry-run. Content-free per-node signal — never carries
// the sampled output, only its shape (status + byte size).
export type DryRunNodeStatus = 'ok' | 'empty' | 'error' | 'synthetic' | 'skipped'

export interface DryRunNodeSignal {
  nodeId: string
  nodeLabel?: string
  nodeType: string
  status: DryRunNodeStatus
  outputSizeBytes: number
  durationMs: number
  message?: string
}

export interface DryRunResult {
  missionId: string
  // 'completed' | 'partial' from the host; 'unavailable' when the dry-run could
  // not run (no host bound, Edge unreachable) — a non-blocking UI state.
  status: 'completed' | 'partial' | 'error' | 'unavailable'
  nodes: DryRunNodeSignal[]
  budget?: {
    tokenBudget: number
    costBudgetCents: number
    timeSeconds: number
  }
}

// ADR-20260621 AA2 diagnose-to-improve wire types.
export type NodeFixKind = 'optimize_prompt' | 'adjust_timeout' | 'reduce_retries' | 'add_error_handling'

export interface GenerateNodeFixRequest {
  nodeId: string // -> node_id
  fixKind: NodeFixKind // -> fix_kind
  diagnosis?: string
}

export interface NodeFixSuggestion {
  id: string
  missionId: string
  targetNodeId: string
  suggestionType: NodeFixKind
  title: string
  description: string
  // proposedPatch is a dag.Node-shaped object (the node after the fix), for diff preview.
  proposedPatch?: unknown
  baseDocVersion: number
  status: string
  stale: boolean
  createdAt: string
}

export interface WorkflowCanonicalizationAction {
  code: string
  message: string
  path?: string
  nodeId?: string
  edgeId?: string
  from?: string
  to?: string
}

// ADR-051 slice 8: mission portability import result. The backend reconnects the
// package's ref_keys (resource/capsule/tool slugs) to this environment and returns
// the reconstructed workflow WITHOUT persisting — the designer loads it for review
// before the user saves. `resolved` is false when any binding/tool needs manual
// rebinding (per-binding detail in `resolutions`).
export interface ImportMissionResponse {
  missionName: string
  // The source mission's objective (mission_prompt). The import flow applies it to
  // the draft so the operator keeps the original objective instead of a generic
  // placeholder (challenge mission-export-behavioral-fidelity, objective-fidelity
  // addendum). Absent when the package carried none.
  missionObjective?: string
  // The source mission's execution mode (adaptive | workflow | autonomous).
  // Authoritative for the imported draft: the workflow graph was lowered for
  // this mode and will not run correctly under another (a Workflow package
  // under an Adaptive row has no orchestrator). Absent when the source carried
  // none. Also travels in workflow.metadata.executionMode — no second source of
  // truth (challenge mission-export-behavioral-fidelity C-8); this is the
  // convenience top-level copy.
  executionMode?: string
  // Behavior-affecting mission-ROW scalars (model + budgets) that are NOT part
  // of the workflow graph. Any subset may be present; the whole object is
  // absent when the source carried none. Absent fields must NOT be sent on save
  // so they don't overwrite the target row with empty/zero. camelCase keys per
  // missiondesign.MissionDefaults (the wire object already uses camelCase under
  // the snake_case `mission_defaults` envelope).
  missionDefaults?: {
    missionType?: string
    orchestratorModelId?: string
    tokenBudget?: number
    costBudgetCents?: number
    timeBudgetSeconds?: number
  }
  workflow: unknown // a dag.Graph-shaped object to load into the designer
  // Deterministic workflow-only storage changes already applied to the preview.
  // The import endpoint does not persist them; the review modal shows the list
  // and the operator's confirmation saves the returned workflow candidate.
  canonicalizationActions?: WorkflowCanonicalizationAction[]
  resolved: boolean
  resolutions: Array<{
    nodeId?: string
    kind: 'resource' | 'capsule' | 'tool'
    slug?: string
    name?: string
    // 'created' = the capsule was recreated on the target host from the
    // packaged schema (capsule-portability-d1) — a success, not a gap.
    // 'compiling' = the tool's compile is still running on the target host
    // and the step is bound to it (agion/Agion#2467). Same union as
    // ResolutionStatus in components/missions/import-resolution-grouping.
    status: 'auto' | 'recompiled' | 'compile_failed' | 'needs_binding' | 'created' | 'compiling'
    localId?: string
    detail?: string
    // Advisory same-connector-type rebind suggestions for needs_binding
    // resources. Never auto-bound — the operator picks in the designer.
    candidates?: Array<{ id: string; name: string; slug?: string }>
  }>
  // Non-blocking operator notices, e.g. prompts that embed source-environment
  // UUIDs which may need manual update after rebinding.
  warnings?: string[]
  // Carried mission state. visibility/successCriteria/textConstraints/constraints/
  // runMode are environment-neutral behavioral context applied on the import save
  // so the imported mission runs with the same orchestration context as the
  // source. governanceFacts/policyConfig are surfaced for operator REVIEW only.
  visibility?: string
  successCriteria?: string[]
  textConstraints?: string[]
  constraints?: unknown // []MissionConstraint
  runMode?: unknown
  governanceFacts?: Record<string, string>
  policyConfig?: unknown
}

/**
 * The served run_metrics of one agent (camelCased), or undefined when the
 * server sent none or a shape it never sends: no figure is made up here.
 */
function readRunMetrics(raw: unknown): AgentRunMetrics | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const { windowDays, executions, successRate, tokens } = raw as Record<string, unknown>
  const count = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0
  if (!count(windowDays) || !count(executions) || !count(tokens)) return undefined
  if (successRate !== null && (typeof successRate !== 'number' || !Number.isFinite(successRate))) return undefined
  return { windowDays, executions, successRate, tokens }
}

// ============= Unified API Client =============

// MISSION_CATALOG_LIMIT is the page size used when a caller needs the full
// mission catalog (member lookups, dropdowns, local search/filter). It matches
// the orchestration list endpoint's lightweight maximum so the catalog is never
// silently truncated. The heavy workflow_definition/design_definition JSONB is
// omitted by the default view, so fetching this many lightweight rows is cheap.
export const MISSION_CATALOG_LIMIT = 5000

export class UnifiedAPIClient {
  private client: KyInstance
  private wsConnections = new Map<string, WebSocket>()
  private authToken: string | null = null

  constructor() {
    this.client = ky.create({
      prefix: `${config.services.api}/v1`,
      // Preserve Ky 1's successful empty-body result for command endpoints.
      parseJson: text => text === '' ? '' : JSON.parse(text),
      timeout: config.performance.apiTimeout,
      credentials: 'include', // Send cookies for session-based auth
      retry: API_RETRY_CONFIG,
      fetch: adapterFetch,
      hooks: {
        beforeRequest: [this.beforeRequest.bind(this)],
        afterResponse: [this.afterResponse.bind(this)],
        beforeError: [this.beforeError.bind(this)]
      }
    })
  }

  // ============= Request/Response Interceptors =============

  private beforeRequest({ request }: BeforeRequestState): void {
    // BFF Pattern: Authentication uses HTTP-only cookies (agion_sso_session)
    // which are automatically sent by the browser with credentials: 'include'
    // APISIX forward-auth plugin resolves session → JWT for upstream services
    // NO Authorization header needed from client

    // Add common headers
    request.headers.set('Content-Type', 'application/json')
    request.headers.set('Accept', 'application/json')
    request.headers.set('X-Requested-With', 'XMLHttpRequest')

    // Add request ID for tracing
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    request.headers.set('X-Request-ID', requestId)

    // Log request in development
    if (config.environment === 'development') {
      logger.debug('API request', {
        method: request.method,
        url: request.url,
        requestId,
        hasAuth: !!this.authToken
      })
    }
  }

  private async afterResponse(
    state: AfterResponseState,
  ): Promise<ReturnType<typeof ky.retry> | void> {
    const { request: _request, response } = state
    if (!response.ok) {
      let errorData: unknown
      let errorMessage = ''
      try {
        errorData = await response.clone().json()
        // Extract message from JSON error responses
        // Prefer 'message' (specific validation text) over 'error' (generic HTTP status text)
        const jsonData = errorData as Record<string, unknown>
        errorMessage = (jsonData?.['message'] || jsonData?.['error'] || '') as string
      } catch {
        // Backend may return plain text errors (e.g. http.Error in Go)
        try {
          errorMessage = (await response.text()).trim()
        } catch {
          errorMessage = response.statusText
        }
        errorData = errorMessage
      }
      // Soft-401 (must run BEFORE throwing) — beforeError only fires for
      // ky's own HTTPError, not for our thrown UnifiedApiError, so the
      // hook below is unreachable on the 401 path. Run it here instead.
      // De-duped across concurrent calls; only redirects on confirmed-gone.
      if (response.status === 401) {
        logger.warn('401 received, probing session via @agion/auth', { url: _request.url })
        void handleUnauthorized()
      }
      const errorCode =
        errorData && typeof errorData === 'object' && typeof (errorData as Record<string, unknown>)['code'] === 'string'
          ? ((errorData as Record<string, unknown>)['code'] as string)
          : undefined
      const comeBack = retryAfterAnswer(state, errorData)
      if (comeBack) return comeBack
      throw new UnifiedApiError(
        response.status,
        errorMessage || response.statusText || `HTTP ${response.status}`,
        errorData,
        errorCode
      )
    }

    // Log successful response in development
    if (config.environment === 'development') {
      const requestId = _request.headers.get('X-Request-ID') || undefined
      logger.debug('API response', {
        status: response.status,
        method: _request.method,
        url: _request.url,
        requestId
      })
    }
  }

  private beforeError({ error }: BeforeErrorState): Error {
    if (!(error instanceof HTTPError)) return adapterError(error)
    // Note: 401 handling moved to afterResponse — see comment there.
    // beforeError only fires when ky generates HTTPError itself, which
    // doesn't happen here because afterResponse pre-empts with our own
    // UnifiedApiError. Kept for any future code path that surfaces
    // an HTTPError directly.
    if (error.response.status === 403) {
      // Handle authorization errors - redirect to unauthorized page
      logger.warn('Forbidden access, redirecting to /unauthorized', { url: error.request.url, status: error.response.status })
      if (window.location.pathname !== '/unauthorized') {
        window.location.href = '/unauthorized'
      }
    } else if (error.response.status >= 500) {
      // Handle server errors
      logger.error('Server error', new Error(`${error.response.status} ${error.response.statusText}`), {
        url: error.request.url,
        status: error.response.status
      })
    }
    return error
  }

  // ============= Authentication Management =============
  // BFF Pattern: These methods are kept for backward compatibility but are no-ops
  // Authentication is handled via HttpOnly cookies (agion_sso_session)

  setAuthToken(_token: string): void {
    // BFF Pattern: No client-side token storage
    console.warn('[API Client] setAuthToken called but BFF pattern is active - token not stored')
  }

  getAuthToken(): string | null {
    // BFF Pattern: No client-side token storage
    // Authentication is handled via HttpOnly cookies
    return null
  }

  clearAuth(): void {
    // BFF Pattern: No client-side auth to clear
    logger.debug('clearAuth called (no-op in BFF pattern)')
  }

  // ============= Generic Resource Operations =============
  // Note: These methods handle case conversion automatically:
  // - Request data: camelCase → snake_case (sent to backend)
  // - Response data: snake_case → camelCase (returned to frontend)

  async get<T>(
    path: string,
    searchParams?: Record<string, string | number | boolean>,
    options?: ApiRequestOptions
  ): Promise<T> {
    // Filter out undefined values from searchParams
    const filteredParams = searchParams
      ? Object.fromEntries(
          Object.entries(searchParams).filter(([_, value]) => value !== undefined && value !== null)
        )
      : undefined
    const response = await this.client
      .get(path, { searchParams: filteredParams, ...requestOverrides(options) })
      .json()
    return keysToCamel<T>(response)
  }

  // Administrative evidence is an opaque document: normalize the envelope
  // only, preserving every stored metadata key/value, including colliding keys.
  async getOrganizationAuditEvents(orgId: string, options: OrganizationAuditOptions = {}): Promise<OrganizationAuditPage> {
    const response = await this.client.get(`organizations/${encodeURIComponent(orgId)}/audit-events`, {
      searchParams: { limit: 50, ...(options.cursor ? { cursor: options.cursor } : {}), ...(options.eventType ? { event_type: options.eventType } : {}) },
    }).json<{ events: Array<Record<string, unknown>>; next_cursor: string | null }>()
    if (!Array.isArray(response.events) || response.events.some(event => event.organization_id !== orgId)) {
      throw new Error('Invalid organization audit response')
    }
    return {
      nextCursor: response.next_cursor,
      events: response.events.map(event => ({
        ...keysToCamel<Omit<OrganizationAuditPage['events'][number], 'metadata'>>({ ...event, metadata: undefined }),
        metadata: event.metadata,
      })),
    }
  }

  async post<T, D = unknown>(path: string, data?: D, options?: ApiRequestOptions): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await this.client.post(path, { json: snakeCaseData, ...requestOverrides(options) }).json()
    return keysToCamel<T>(response)
  }

  async patch<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await this.client.patch(path, { json: snakeCaseData }).json()
    return keysToCamel<T>(response)
  }

  async put<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await this.client.put(path, { json: snakeCaseData }).json()
    return keysToCamel<T>(response)
  }

  async delete<T = void>(path: string): Promise<T> {
    const response = await this.client.delete(path).json()
    return keysToCamel<T>(response)
  }

  /**
   * A binary attachment (a zip, a file) with the name the server's
   * Content-Disposition gives it, else fallbackName. The body is read as a
   * Blob, never as JSON; a refusal still throws the JSON-bodied failure.
   */
  async getFile(path: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.get(path, { retry: 0 })
    const blob = await response.blob()
    const match = /filename="?([^"]+)"?/.exec(response.headers.get('content-disposition') ?? '')
    return { blob, filename: match?.[1] ?? fallbackName }
  }

  // ============= Agent Resource Operations =============

  // CRUD Operations
  async getAgents(params?: {
    includeEventCounts?: boolean
    /** Ask for stats.trust_tiers, the agents of the whole list per trust tier (agion/Agion#2522). */
    includeTierCounts?: boolean
    /** Ask for each agent's run_metrics, its runs, success rate and tokens over 30 days (PLAN-20260912 slice 24). */
    includeRunMetrics?: boolean
    roleView?: 'mission'
    /** Also list the built-in Platform Assistant (agion/Agion#2651); the server grants it to owners and admins only. */
    includePlatformManaged?: boolean
    status?: string
    framework?: string
    trustTier?: number
    page?: number
    limit?: number
    offset?: number
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    capabilities?: string[]
    role?: 'orchestrator' | 'specialist'
    /** Only the agents of this mission (the server's mission_id filter). */
    missionId?: string
  }): Promise<AgentListResponse> {
    // Use the new discovery endpoint with manifest support
    const queryParams: Record<string, string | number> = {}

    if (params?.status) queryParams['status'] = params.status
    if (params?.framework) queryParams['framework'] = params.framework
    if (params?.search) queryParams['search'] = params.search
    if (params?.limit) queryParams['limit'] = params.limit
    if (params?.offset !== undefined) {
      queryParams['offset'] = params.offset
    } else if (params?.page) {
      queryParams['offset'] = (params.page - 1) * (params.limit || 100)
    }
    if (params?.sortBy) queryParams['sort_by'] = params.sortBy
    if (params?.sortOrder) queryParams['sort_order'] = params.sortOrder
    if (params?.capabilities && params.capabilities.length > 0) {
      queryParams['capabilities'] = params.capabilities.join(',')
    }
    if (params?.role) queryParams['role'] = params.role
    if (params?.missionId) queryParams['mission_id'] = params.missionId
    if (params?.trustTier !== undefined) queryParams['trust_tier'] = params.trustTier
    if (params?.includeEventCounts) queryParams['include_event_counts'] = 'true'
    if (params?.includeTierCounts) queryParams['include_tier_counts'] = 'true'
    if (params?.includeRunMetrics) queryParams['include_run_metrics'] = 'true'
    if (params?.roleView) queryParams['role_view'] = params.roleView
    if (params?.includePlatformManaged) queryParams['include_platform_managed'] = 'true'
    const response = await this.get<AgentDiscoveryResponse>('agents', queryParams)

    // Map discovery API response to AgentListResponse format
    // Note: Backend execution-service returns 'id' field, but AgentInfo type expects 'agentId'
    // We need to handle both field names for compatibility
    const agents: Agent[] = (response.agents || []).map(agent => {
      // Handle both 'id' (from execution-service) and 'agentId' (from discovery API)
      const agentId = agent.agentId || (agent as any).id
      // Extract fields from backend response (snake_case from governance-service)
      const backendAgent = agent as any
      const assignment: AgentMissionAssignment | null | undefined = params?.roleView === 'mission'
        ? backendAgent.missionAssignment ?? backendAgent.mission_assignment ?? null : undefined
      if (assignment && assignment.role !== 'orchestrator' && assignment.role !== 'specialist') {
        throw new Error('Agent mission assignment is unavailable')
      }
      const eventCount = (camel: string, snake: string): number | undefined => {
        const value = backendAgent[camel] ?? backendAgent[snake]
        return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined
      }
      const runMetrics = readRunMetrics(backendAgent.runMetrics)
      const displayName = backendAgent.display_name || backendAgent.displayName
      const sourceName = backendAgent.source_name || backendAgent.sourceName
      const agentType = backendAgent.agent_type || backendAgent.agentType || (sourceName === 'declarative' ? 'declarative' : null) // declarative | code
      const hostType = backendAgent.host_type || backendAgent.hostType // platform | byoh
      const hostId = backendAgent.default_host_id || backendAgent.defaultHostId || backendAgent.host_id || backendAgent.hostId
      const hostName = backendAgent.host_name || backendAgent.hostName

      // Determine language from framework or manifest
      const framework = agent.framework?.toLowerCase()
      let language: 'python' | 'go' | 'typescript' | undefined
      if (framework === 'langchain' || framework === 'langgraph' || framework === 'crewai' || framework === 'autogen') {
        language = 'python'
      } else if (framework === 'go') {
        language = 'go'
      } else if (framework === 'typescript' || framework === 'nodejs') {
        language = 'typescript'
      }

      return {
        did: agentId,
        id: agentId,
        name: agent.name,
        displayName: displayName || agent.name,
        description: agent.description || '',
        type: agent.capabilities?.[0] || 'unknown',

        // New agent classification model - prefer explicit agent_type, fall back to source_name, then framework
        agentType: (agentType || (sourceName === 'declarative' ? 'declarative' : null) || (agent.framework === 'declarative' ? 'declarative' : 'code')) as 'declarative' | 'code',
        language,
        hostType: hostType as 'platform' | 'byoh' || 'platform',
        hostId,
        hostName: hostName || (hostType === 'platform' || !hostType ? 'Agion Platform' : undefined),

        // Legacy fields for backwards compatibility
        framework: (agent.framework as AgentFramework) || (agentType === 'declarative' ? 'declarative' : 'custom'),
        sourceName: agentType === 'declarative' ? 'declarative' : (agent.framework || 'code'),
        isSystemHosted: hostType === 'platform' || !hostType,

        lifecycleState: agent.healthStatus === 'healthy' ? 'running' : 'idle' as AgentLifecycleState,
        status: agent.status as AgentStatus || 'idle',
        // The tier the server gave the score; never a tier the browser picks (agion/Agion#3140).
        trustTier: (backendAgent.trustTierName || backendAgent.trust_tier_name || undefined) as TrustTier | undefined,
        trustScore: backendAgent.trustScore ?? backendAgent.trust_score ?? 0.0,
        positiveEvents: eventCount('positiveEvents', 'positive_events'),
        negativeEvents: eventCount('negativeEvents', 'negative_events'),
        ...(runMetrics ? { runMetrics } : {}),
        complianceStatus: 'approved' as const,
        environment: {},
        tools: [],
        capabilities: agent.capabilities || [],
        a2aEnabled: false,
        resources: {
          cpuMillicores: 0,
          memoryBytes: 0
        },
        owner: 'system',
        createdAt: new Date().toISOString(),
        updatedAt: agent.lastHeartbeat || new Date().toISOString(),
        endpointUrl: agent.endpointUrl,
        healthStatus: agent.healthStatus || 'unknown',
        manifest: agent.metadata,
        ...(assignment !== undefined ? { missionAssignment: assignment } : {}),
        missionId: assignment !== undefined ? assignment?.id : backendAgent.missionId || backendAgent.mission_id || backendAgent.effectiveMissionId || backendAgent.effective_mission_id || agent.metadata?.['assignedMissionId'] as string | undefined,
        assignedMissionId: assignment !== undefined ? assignment?.id : backendAgent.missionId || backendAgent.mission_id || backendAgent.effectiveMissionId || backendAgent.effective_mission_id || agent.metadata?.['assignedMissionId'] as string | undefined,
        missionName: assignment !== undefined ? assignment?.name : backendAgent.missionName || backendAgent.mission_name || undefined,
        missionRole: assignment !== undefined ? assignment?.role : backendAgent.missionRole || backendAgent.mission_role || undefined,
        missionStatus: assignment !== undefined ? assignment?.status : backendAgent.missionStatus || backendAgent.mission_status || undefined,
        modelId: backendAgent.modelId || backendAgent.model_id || backendAgent.effectiveModelId || backendAgent.effective_model_id || undefined,
        voice: backendAgent.voice || undefined,
        interactionChannel: backendAgent.interactionChannel || backendAgent.interaction_channel || undefined,
      }
    })

    return {
      agents,
      total: response.total || agents.length,
      page: params?.page || 1,
      limit: params?.limit || 100,
      stats: (response as any).stats || undefined,
    }
  }

  /**
   * Inventory observations over the agents the list shows the caller; this
   * endpoint accepts no list filters. A scoped viewer's daily history is empty.
   */
  async getAgentStatistics(): Promise<AgentStatistics> {
    const response = await this.get<Omit<AgentStatistics, 'delta7d'> & { delta_7d: AgentStatistics['delta7d'] }>('agents/statistics')
    if (response.roleView !== 'mission'
      || (response.historyBasis !== 'latest_daily_observation' && response.historyBasis !== 'not_available_for_scoped_viewer')
      || response.windowDays !== 7 || !response.current || !Array.isArray(response.daily)) {
      throw new Error('Agent statistics are unavailable')
    }
    // The shared converter intentionally leaves underscores before digits intact.
    return { ...response, delta7d: response.delta_7d ?? null }
  }

  async getAgent(did: string): Promise<AgentWithDetails> {
    const agent = await this.get<AgentWithDetails>(`agents/${encodeURIComponent(did)}`)
    // Normalize agent ID fields - API returns agentId, but code expects did/id
    const agentId = (agent as any).agentId || agent.did || agent.id || did

    // Extract fields from backend response (snake_case from governance-service)
    const backendAgent = agent as any
    const displayName = backendAgent.display_name || backendAgent.displayName
    const sourceName = backendAgent.source_name || backendAgent.sourceName
    const agentTypeRaw = backendAgent.agent_type || backendAgent.agentType || (sourceName === 'declarative' ? 'declarative' : null) // declarative | code
    const hostTypeRaw = backendAgent.host_type || backendAgent.hostType // platform | byoh
    const hostId = backendAgent.default_host_id || backendAgent.defaultHostId || backendAgent.host_id || backendAgent.hostId
    const hostName = backendAgent.host_name || backendAgent.hostName

    // Determine language from framework
    const framework = agent.framework?.toLowerCase()
    let language: 'python' | 'go' | 'typescript' | undefined
    if (framework === 'langchain' || framework === 'langgraph' || framework === 'crewai' || framework === 'autogen') {
      language = 'python'
    } else if (framework === 'go') {
      language = 'go'
    } else if (framework === 'typescript' || framework === 'nodejs') {
      language = 'typescript'
    }

    const agentType = (agentTypeRaw || (sourceName === 'declarative' ? 'declarative' : null) || (agent.framework === 'declarative' ? 'declarative' : 'code')) as 'declarative' | 'code'
    const hostType = hostTypeRaw as 'platform' | 'byoh' || 'platform'

    return {
      ...agent,
      did: agent.did || agentId,
      id: agent.id || agentId,
      displayName: displayName || agent.name,

      // New agent classification model
      agentType,
      language,
      hostType,
      hostId,
      hostName: hostName || (hostType === 'platform' ? 'Agion Platform' : undefined),

      // Legacy fields for backwards compatibility
      framework: agent.framework || (agentType === 'declarative' ? 'declarative' : 'custom') as any,
      sourceName: agentType === 'declarative' ? 'declarative' : (agent.framework || 'code'),
      isSystemHosted: hostType === 'platform'
    }
  }

  async createAgent(data: AgentRegistrationRequest): Promise<Agent> {
    return this.post<Agent>('agents', data)
  }

  async updateAgent(did: string, data: Partial<Agent>): Promise<Agent> {
    return this.patch<Agent>(`agents/${encodeURIComponent(did)}`, data)
  }

  async updateDeclarativeAgent(id: string, data: {
    model_id?: string
    system_prompt?: string
    display_name?: string
    description?: string
    tool_bindings?: string[]
  }): Promise<Agent> {
    return this.put<Agent>(`agents/declarative/${encodeURIComponent(id)}`, data)
  }

  async deleteAgent(did: string): Promise<void> {
    return this.delete(`agents/${encodeURIComponent(did)}`)
  }

  // Sub-resource Operations
  async getAgentMetrics(did: string): Promise<AgentMetrics> {
    return this.get<AgentMetrics>(`agents/${encodeURIComponent(did)}/metrics`)
  }

  async getAgentTasks(did: string, status?: string): Promise<AgentTask[]> {
    return this.get<AgentTask[]>(`agents/${encodeURIComponent(did)}/tasks`, status ? { status } : undefined)
  }

  async createAgentTask(did: string, task: {
    type: string
    payload: Record<string, unknown>
    priority?: number
  }): Promise<AgentTask> {
    return this.post<AgentTask>(`agents/${encodeURIComponent(did)}/tasks`, task)
  }

  async getAgentLogs(did: string, lines = 100): Promise<string[]> {
    const response = await this.get<{ logs: string[] }>(
      `agents/${encodeURIComponent(did)}/logs`,
      { lines }
    )
    return response.logs
  }

  // Lifecycle Operations
  async deployAgent(did: string, deployment: AgentDeploymentRequest): Promise<Agent> {
    return this.post<Agent>(`agents/${encodeURIComponent(did)}/deploy`, deployment)
  }

  async startAgent(did: string): Promise<Agent> {
    return this.post<Agent>(`agents/${encodeURIComponent(did)}/start`)
  }

  async stopAgent(did: string): Promise<Agent> {
    return this.post<Agent>(`agents/${encodeURIComponent(did)}/stop`)
  }

  async transitionAgentState(did: string, state: AgentLifecycleState, reason?: string): Promise<Agent> {
    return this.patch<Agent>(`agents/${encodeURIComponent(did)}/lifecycle`, { desired_state: state, reason })
  }

  // ============= Mission Resource Operations =============

  // CRUD Operations - Using V2 API for accurate agent/member data
  //
  // The list endpoint is paginated and returns a small default page; callers of
  // this method (member lookups, mission dropdowns) expect EVERY mission, so we
  // request the full lightweight catalog explicitly. The server's default view
  // omits the heavy workflow_definition/design_definition JSONB, so a large page
  // is cheap — and MISSION_CATALOG_LIMIT matches the server's lightweight cap so
  // nothing is silently truncated.
  async getMissions(): Promise<Mission[]> {
    const response = await this.get<{ missions: Mission[]; total: number }>('missions', {
      limit: MISSION_CATALOG_LIMIT,
    })
    return response.missions || []
  }

  async getMission(id: string): Promise<Mission> {
    return this.get<Mission>(`missions/${id}`)
  }

  async createMission(data: CreateMissionRequest): Promise<Mission> {
    return this.post<Mission>('missions', data)
  }

  async updateMission(id: string, data: UpdateMissionRequest): Promise<Mission> {
    return this.patch<Mission>(`missions/${id}`, data)
  }

  // ADR-051 slice 3/9: scoped single-node patch. Sends one node's new content
  // (not the whole document) so edit-time cost stays bounded. The generic patch()
  // adapter converts baseDocVersion -> base_doc_version on the wire and camelizes
  // the response. A stale base_doc_version surfaces as a 409 HTTPError.
  async patchMissionNode(
    missionId: string,
    nodeId: string,
    data: PatchMissionNodeRequest,
  ): Promise<PatchMissionNodeResponse> {
    // The `node` payload is a dag.Node whose JSON tags are camelCase
    // (agentConfig, systemPrompt, connectedResourceIds). It must travel verbatim
    // — running it through keysToSnake would corrupt those keys and the backend
    // could not unmarshal the node. So we build the wire body by hand: snake-case
    // only the wrapper key (base_doc_version) and leave `node` untouched.
    const body = {
      node: data.node,
      source: data.source,
      base_doc_version: data.baseDocVersion,
      ...(data.classificationConfirmation ? { classification_confirmation: data.classificationConfirmation } : {}),
    }
    try {
      const response = await this.client
        .patch(`missions/${missionId}/nodes/${nodeId}`, { json: body })
        .json()
      return keysToCamel<PatchMissionNodeResponse>(response)
    } catch (err: unknown) {
      // 409 = optimistic-concurrency conflict (the mission's doc_version advanced
      // since this base). Surface it as a conflict outcome rather than a thrown
      // error so callers show the accurate "reload to get the latest version"
      // message instead of a generic save failure.
      // The client's afterResponse hook turns every non-2xx answer into a
      // UnifiedApiError (status + parsed body) before ky's own HTTPError
      // (ADR-20260925 rule 3, agion/Agion#2273).
      const status = isApiFailure(err) ? err.status : undefined
      if (status === 409) {
        const detail = (isApiFailure(err) ? err.data : undefined) as
          | ({ error?: string } & Record<string, unknown>)
          | undefined
        if (detail?.error === 'mission_classification_confirmation_required') {
          throw new UnifiedApiError(409, detail.error, detail)
        }
        // A coded classification refusal (adoption_blocked) is the server's
        // verdict on the mission, not a stale document: the caller words it
        // and neither rebases nor retries (agion/Agion#2875).
        if (classificationWriteFailureOf(err).kind !== 'other') throw err
        const current = detail
          ? keysToCamel<{ currentDocVersion?: unknown }>(detail).currentDocVersion
          : undefined
        return {
          missionId,
          nodeId,
          applied: false,
          conflict: true,
          conflictNodeId: nodeId,
          docVersion: data.baseDocVersion,
          versionNumber: 0,
          ...(typeof current === 'number' && Number.isSafeInteger(current) ? { currentDocVersion: current } : {}),
        }
      }
      // The server's pending answer: the edit is stored, so it is applied at
      // the version the server names (agion/Agion#2677).
      const pending = missionWritePermissionPending(err)
      if (pending) {
        return {
          missionId,
          nodeId,
          applied: true,
          conflict: false,
          permissionPending: true,
          docVersion: pending.docVersion ?? data.baseDocVersion,
          versionNumber: pending.versionNumber ?? 0,
        }
      }
      // 503/502/504 = transient backend contention (e.g. the missions row is
      // locked by a concurrently-running mission and the scoped patch fast-
      // failed). The edit is not lost — surface a transient outcome so the
      // designer keeps it locally and reports "held locally", not a hard
      // failure. The base doc_version is unchanged, so a later retry re-bases
      // cleanly.
      if (status === 503 || status === 502 || status === 504) {
        return {
          missionId,
          nodeId,
          applied: false,
          conflict: false,
          transient: true,
          docVersion: data.baseDocVersion,
          versionNumber: 0,
        }
      }
      throw err
    }
  }

  // Semantic dry-run (ADR-20260621 AA1). Triggers a read-only preview on the
  // mission's Edge host and returns only a content-free per-node signal. Never
  // throws for the expected "unavailable" cases (no host bound, Edge unreachable):
  // those resolve to { status: 'unavailable' } so the designer shows a
  // non-blocking banner instead of an error. Authoring is never blocked.
  async dryRunMission(missionId: string): Promise<DryRunResult> {
    try {
      const response = await this.client.post(`missions/${missionId}/dry-run`).json()
      return keysToCamel<DryRunResult>(response)
    } catch (err: unknown) {
      // agion/Agion#2273: the hook's UnifiedApiError carries the status.
      const status = isApiFailure(err) ? err.status : undefined
      if (status === 503 || status === 502) {
        return { missionId, status: 'unavailable', nodes: [] }
      }
      throw err
    }
  }

  // ADR-20260621 AA2 diagnose-to-improve. Generate a structured one-click fix for
  // a node, list a mission's fixes, apply (via the scoped node-patch), undo, or
  // dismiss. proposedPatch is a dag.Node-shaped object (camelCase) returned for
  // diff preview before apply. Apply surfaces a 409 as a stale-suggestion error.
  async generateNodeFix(missionId: string, req: GenerateNodeFixRequest): Promise<NodeFixSuggestion> {
    return this.post<NodeFixSuggestion>(`missions/${missionId}/suggestions`, req)
  }

  async listNodeFixes(missionId: string, status?: string): Promise<NodeFixSuggestion[]> {
    const res = await this.get<{ suggestions: NodeFixSuggestion[]; count: number }>(
      `missions/${missionId}/suggestions`,
      status ? { status } : undefined,
    )
    return res.suggestions || []
  }

  async applyNodeFix(missionId: string, suggestionId: string): Promise<PatchMissionNodeResponse> {
    return this.writeNodeFix(missionId, suggestionId, 'apply')
  }

  async undoNodeFix(missionId: string, suggestionId: string): Promise<PatchMissionNodeResponse> {
    return this.writeNodeFix(missionId, suggestionId, 'undo')
  }

  // The server's pending answer to a node-fix apply or undo: the node patch
  // is stored and the suggestion records it, so it is applied at the version
  // the server names (agion/Agion#2677). The answer names no node.
  private async writeNodeFix(missionId: string, suggestionId: string, action: 'apply' | 'undo'): Promise<PatchMissionNodeResponse> {
    try {
      const response = await this.client
        .post(`missions/${missionId}/suggestions/${suggestionId}/${action}`)
        .json()
      return keysToCamel<PatchMissionNodeResponse>(response)
    } catch (err: unknown) {
      const pending = missionWritePermissionPending(err)
      if (pending?.docVersion === undefined) throw err
      return {
        missionId,
        nodeId: '',
        applied: true,
        conflict: false,
        permissionPending: true,
        docVersion: pending.docVersion,
        versionNumber: pending.versionNumber ?? 0,
      }
    }
  }

  async dismissNodeFix(missionId: string, suggestionId: string, reason?: string): Promise<void> {
    await this.post<void>(`missions/${missionId}/suggestions/${suggestionId}/dismiss`, { reason: reason || '' })
  }

  deleteMission = (id: string) => requestMissionDeletion(this, id)
  removeMissionAgent = (missionId: string, agentId: string) => requestMissionAgentRemoval(this, missionId, agentId)

  // ADR-051 slice 8: download the open mission as a portable .agionpkg package
  // (ZIP: mission.json + tool source). The backend emits a binary attachment; we
  // surface the raw blob + server-suggested filename so the caller can save it.
  //
  // ADR-20260621 slice 5: enriched with export-readiness headers.
  //   X-Agion-Export-Coherence: complete | degraded | blocked
  //   X-Agion-Export-Unverified: true (when host unreachable)
  // A 'blocked' export returns 422 — the caller's catch path handles that.
  //
  // BINARY-DOWNLOAD-PATH GUARD (ADR issue 984):
  // This method deliberately bypasses the JSON+camelCase api-client wrapper.
  // The .agionpkg archive is raw binary; routing it through get<T>() or any
  // JSON parse would corrupt the archive. Do NOT refactor this to use the
  // generic get/post helpers. The unit test mission-export-coherence.test.ts
  // asserts the response is a Blob.
  async exportMission(id: string): Promise<ExportMissionResult> {
    const response = await this.client.get(`missions/${id}/export`)
    // MUST call .blob(), not .json() — the response is a ZIP archive.
    const blob = await response.blob()
    const disposition = response.headers.get('content-disposition') ?? ''
    const match = /filename="?([^"]+)"?/.exec(disposition)
    const filename = match?.[1] ?? `mission-${id}.agionpkg`
    const { coherence, unverified } = parseExportCoherenceHeaders(response.headers)
    return { blob, filename, coherence, unverified }
  }

  // ADR-051 slice 8: import a portable .agionpkg package. The raw archive bytes
  // are POSTed as the request body; the backend reconnects ref_keys against this
  // environment's catalog and returns the reconstructed workflow + report (it does
  // NOT persist — the designer loads the workflow for review before save). When a
  // concrete target host is given, generated tools are recompiled there so the
  // imported mission is publishable/runnable (auto-recompile-on-import).
  async importMission(
    pkg: Blob,
    missionId: string,
    hostId?: string
  ): Promise<ImportMissionResponse> {
    // ADR-20260610 slice 2 (commit B2): imported tools are mission-owned, so the
    // import target must exist before import — the caller creates the draft
    // mission and imports into it; the backend rejects calls without mission_id.
    const searchParams: Record<string, string> = { mission_id: missionId }
    if (hostId) searchParams.host_id = hostId
    const response = await this.client.post('missions/import', { body: pkg, searchParams }).json()
    const result = keysToCamel<ImportMissionResponse>(response)
    // The import review groups the resolutions as soon as it opens. An answer
    // without the list is refused here, where the caller still cleans up its
    // import draft (agion/Agion#1780).
    if (!Array.isArray(result?.resolutions)) {
      throw new Error('The import answer has no resolutions list; the server and admin-ui versions may differ.')
    }
    return result
  }

  // Sub-resource Operations
  async getMissionState(id: string): Promise<MissionState> {
    return this.get<MissionState>(`missions/${id}/state`)
  }

  async getMissionMetrics(id: string, window?: '7d' | '30d'): Promise<Record<string, unknown>> {
    if (!window) return this.get<Record<string, unknown>>(`missions/${id}/metrics`)
    return this.get<Record<string, unknown>>(`missions/${id}/metrics`, { window })
  }

  async getMissionHistory(id: string, limit = 100): Promise<Array<Record<string, unknown>>> {
    return this.get<Array<Record<string, unknown>>>(`missions/${id}/history`, { limit })
  }

  // Mission Control Operations (V1 - deprecated, use V2 lifecycle methods instead)
  async executeMission(id: string): Promise<MissionState> {
    return this.post<MissionState>(`missions/${id}/execute`)
  }

  async pauseMission(id: string): Promise<MissionState> {
    return this.post<MissionState>(`missions/${id}/pause`)
  }

  async resumeMission(id: string): Promise<MissionState> {
    return this.post<MissionState>(`missions/${id}/resume`)
  }

  async abortMission(id: string): Promise<MissionState> {
    return this.post<MissionState>(`missions/${id}/abort`)
  }

  // V2 Mission Lifecycle Operations
  /**
   * Launch a mission (transitions from draft → active)
   * This provisions the orchestrator and activates the mission.
   *
   * The mission runs on the host bound to the mission record. Callers
   * cannot redirect a mission to a different host at launch time — to
   * change where a mission runs, update the mission's host first.
   *
   * @param id - Mission ID
   * @param options.input - Optional start values, keyed by the names the mission declares (e.g., caller_phone, ObjectID)
   */
  async launchMission(id: string, options?: { input?: Record<string, unknown>; runtimePackage?: boolean; previewVersionId?: string; runTarget?: string }): Promise<{ execution_id: string }> {
    // Start values are user data: the server matches them by the exact names
    // the mission declares, and an object value keeps its own keys. Running
    // them through keysToSnake turned ObjectID into _object_i_d, so a start
    // value named with a capital letter could never be supplied
    // (agion/Agion#1780). The wire body is built by hand, as patchMissionNode
    // does, with only the wrapper keys in snake_case.
    const body: Record<string, unknown> = {}
    if (options?.input) body['input'] = options.input
    // run_target is the explicit version selector: 'draft' forces the live
    // draft (designer Run), 'version' runs preview_version_id. A bare launch
    // (no run_target) lets the server run the active published version.
    if (options?.runTarget) body['run_target'] = options.runTarget
    if (options?.runtimePackage) body['runtime_package'] = true
    if (options?.previewVersionId) body['preview_version_id'] = options.previewVersionId
    const response = await this.client
      .post(`missions/${id}/launch`, { json: Object.keys(body).length > 0 ? body : undefined })
      .json()
    return keysToCamel(response)
  }

  /**
   * Deactivate a mission (pauses an active mission)
   * Transitions from active → inactive.
   * @param id - Mission ID
   */
  async deactivateMission(id: string): Promise<void> {
    await this.post<void>(`missions/${id}/deactivate`)
  }

  /**
   * Reactivate a mission (resumes an inactive mission)
   * Transitions from inactive → active.
   * @param id - Mission ID
   */
  async reactivateMission(id: string): Promise<void> {
    await this.post<void>(`missions/${id}/reactivate`)
  }

  /**
   * Archive a mission (permanently stops the mission)
   * Transitions to archived (terminal state).
   * @param id - Mission ID
   */
  async archiveMission(id: string): Promise<void> {
    await this.post<void>(`missions/${id}/archive`)
  }

  /**
   * Provision an orchestrator for an existing mission
   * Used for platform missions created via migrations.
   * @param id - Mission ID
   * @returns The provisioned orchestrator agent details
   */
  async provisionMissionOrchestrator(id: string): Promise<Agent> {
    return this.post<Agent>(`missions/${id}/provision-orchestrator`)
  }

  // ============= Agent Mission Management =============

  /**
   * Move agent from sandbox to a mission
   * @param agentId - Agent DID to move
   * @param targetMissionId - Target mission ID
   */
  async moveAgent(agentId: string, targetMissionId: string): Promise<void> {
    return this.post<void>(`agents/${encodeURIComponent(agentId)}/move`, {
      target_mission_id: targetMissionId
    })
  }

  /**
   * Duplicate agent from one mission to another
   * @param agentId - Source agent DID
   * @param targetMissionId - Target mission ID
   * @param newName - Optional new name for duplicated agent
   * @returns The newly created agent
   */
  async duplicateAgent(agentId: string, targetMissionId: string, newName?: string): Promise<Agent> {
    return this.post<Agent>(`agents/${encodeURIComponent(agentId)}/duplicate`, {
      target_mission_id: targetMissionId,
      new_name: newName
    })
  }

  /**
   * Remove agent from its current mission (returns to owner's sandbox)
   * @param agentId - Agent DID to remove
   */
  async removeAgentFromMission(agentId: string): Promise<void> {
    return this.delete<void>(`agents/${encodeURIComponent(agentId)}/mission`)
  }

  /**
   * Get agents for picker (includes all accessible agents)
   * @param params - Query parameters
   * @returns Agent picker response with source information
   */
  async getAgentsForPicker(params: { includeAllAccessible: boolean }): Promise<AgentListResponse> {
    return this.get<AgentListResponse>('agents/picker', params as Record<string, boolean>)
  }

  // ============= Mission Participant Management =============

  async addMissionParticipant(
    missionId: string,
    req: { userId: string; role: 'owner' | 'member' }
  ): Promise<MissionParticipant> {
    return this.post<MissionParticipant>(`missions/${missionId}/members`, req)
  }

  async getMissionParticipants(
    missionId: string,
    page?: number
  ): Promise<PaginatedResponse<MissionParticipant>> {
    return this.get<PaginatedResponse<MissionParticipant>>(
      `missions/${missionId}/participants`,
      page ? { page } : undefined
    )
  }

  async updateMissionParticipant(
    missionId: string,
    userId: string,
    role: 'owner' | 'member'
  ): Promise<void> {
    return this.patch<void>(`missions/${missionId}/members/${userId}`, { role })
  }

  async removeMissionParticipant(missionId: string, userId: string): Promise<void> {
    return this.delete<void>(`missions/${missionId}/members/${userId}`)
  }

  /**
   * Transfer mission ownership to another user
   * @param missionId - Mission ID
   * @param newOwnerId - New owner's user ID, sent as `new_owner_id`, the field
   *   orchestration TransferOwnership reads (agion/Agion#3064)
   */
  async transferMissionOwnership(missionId: string, newOwnerId: string): Promise<void> {
    return this.post<void>(`missions/${missionId}/transfer-ownership`, {
      new_owner_id: newOwnerId
    })
  }

  // ============= Resource Operations =============
  // NOTE: Resources are managed by governance-service at /v1/governance/resources

  async getResources(params?: {
    type?: string
    subtype?: string
    status?: string
  }): Promise<{
    resources: Array<{
      id: string
      uri: string
      name: string
      type: string
      subtype?: string
      status?: string
      fields?: Array<{ name: string; value: string | number | boolean | null; type?: string }>
      metadata?: Record<string, unknown>
    }>
    total: number
  }> {
    return this.get('governance/resources', params as Record<string, string>)
  }

  async getResource(id: string): Promise<Record<string, unknown>> {
    return this.get(`governance/resources/${id}`)
  }

  async createResource(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('governance/resources', data)
  }

  async updateResource(id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.put(`governance/resources/${id}`, data)
  }

  async deleteResource(id: string): Promise<void> {
    return this.delete(`governance/resources/${id}`)
  }

  // ============= Runtime Host Operations =============

  async getHosts(framework?: string): Promise<RuntimeHost[]> {
    return this.get<RuntimeHost[]>('hosts', framework ? { framework } : undefined)
  }

  async getHost(did: string): Promise<RuntimeHost> {
    return this.get<RuntimeHost>(`hosts/${encodeURIComponent(did)}`)
  }

  async selectHost(agentDid: string): Promise<RuntimeHost> {
    return this.get<RuntimeHost>('hosts/select', { agent_did: agentDid })
  }

  // ============= Task Operations =============

  async getTask(taskId: string): Promise<AgentTask> {
    return this.get<AgentTask>(`tasks/${taskId}`)
  }

  async cancelTask(taskId: string): Promise<void> {
    return this.post(`tasks/${taskId}/cancel`)
  }

  // ============= Governance Operations =============

  async submitAgentForReview(did: string, notes?: string): Promise<GovernanceReview> {
    return this.post<GovernanceReview>(`governance/agents/${encodeURIComponent(did)}/review`, { notes })
  }

  async approveAgent(did: string, trustTier: number, notes: string): Promise<GovernanceReview> {
    return this.post<GovernanceReview>(`governance/agents/${encodeURIComponent(did)}/approve`, {
      trust_tier: trustTier,
      notes
    })
  }

  async rejectAgent(did: string, notes: string): Promise<GovernanceReview> {
    return this.post<GovernanceReview>(`governance/agents/${encodeURIComponent(did)}/reject`, { notes })
  }

  // ============= A2A Communication =============

  async sendA2AMessage(
    fromDid: string,
    toDid: string,
    type: string,
    payload: Record<string, unknown>
  ): Promise<A2AMessage> {
    return this.post<A2AMessage>('a2a/messages', {
      from_did: fromDid,
      to_did: toDid,
      type,
      payload
    })
  }

  async getA2AMessages(agentDid: string, limit = 100): Promise<A2AMessage[]> {
    return this.get<A2AMessage[]>(`agents/${encodeURIComponent(agentDid)}/a2a/messages`, { limit })
  }

  // ============= WebSocket Operations =============

  subscribeToResource<T = unknown>(
    resource: string,
    resourceId: string,
    onMessage: (message: WebSocketMessage<T>) => void,
    onError?: (error: Event) => void
  ): () => void {
    const wsUrl = config.services.websocket.replace('http', 'ws')
    const url = `${wsUrl}/${resource}/${encodeURIComponent(resourceId)}/subscribe`
    const connectionKey = `${resource}:${resourceId}`

    // Close existing connection if any
    this.closeWebSocketConnection(connectionKey)

    const ws = new WebSocket(url)

    ws.onopen = () => {
      if (config.environment === 'development') {
        logger.debug('WebSocket connected', { resource, resourceId })
      }
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage<T>
        onMessage(message)
      } catch (error) {
        logger.error('WebSocket failed to parse message', error as Error, { resource, resourceId, data: event.data })
      }
    }

    ws.onerror = (error) => {
      logger.error('WebSocket error', error as unknown as Error, { resource, resourceId })
      onError?.(error)
    }

    ws.onclose = () => {
      if (config.environment === 'development') {
        logger.debug('WebSocket disconnected', { resource, resourceId })
      }
      this.wsConnections.delete(connectionKey)
    }

    this.wsConnections.set(connectionKey, ws)

    // Return cleanup function
    return () => this.closeWebSocketConnection(connectionKey)
  }

  subscribeToAgent(did: string, onUpdate: (agent: Agent) => void): () => void {
    return this.subscribeToResource<Agent>('agents', did, (message) => {
      if (message.type === 'agent_update') {
        onUpdate(message.data)
      }
    })
  }

  subscribeToMission(id: string, onUpdate: (state: MissionState) => void): () => void {
    return this.subscribeToResource<MissionState>('missions', id, (message) => {
      if (message.type === 'mission_state_update') {
        onUpdate(message.data)
      }
    })
  }

  private closeWebSocketConnection(connectionKey: string): void {
    const ws = this.wsConnections.get(connectionKey)
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close()
    }
    this.wsConnections.delete(connectionKey)
  }

  // ============= File Upload Operations =============

  async uploadFile<T>(path: string, file: File): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    return this.client.post(path, {
      body: formData,
      hooks: {
        beforeRequest: [
          ({ request }) => {
            // Remove Content-Type for file uploads (let browser set it)
            request.headers.delete('Content-Type')
            // BFF Pattern: No Authorization header needed
            // Authentication is handled by agion_sso_session cookie
          }
        ]
      }
    }).json<T>()
  }

  // ============= User Management =============

  /**
   * Update user profile (first_name, last_name, preferred_language).
   * preferred_language must be 'en' | 'fi' | 'sv' | 'fr' | 'de' — the auth-service rejects
   * other codes with 400. Persists into agion.users.preferences->>'language'.
   * @param userId - User ID to update (or "me" to resolve via session)
   * @param data - Profile data to update
   * @returns Updated user data
   */
  async updateUserProfile(
    userId: string,
    data: {
      first_name?: string
      last_name?: string
      preferred_language?: 'en' | 'fi' | 'sv' | 'fr' | 'de'
    }
  ): Promise<{
    id: string
    email: string
    first_name: string | null
    last_name: string | null
    full_name: string | null
    is_active: boolean
    preferred_language: string | null
  }> {
    return this.patch(`users/${userId}`, data)
  }

  /**
   * Change user password
   * @param userId - User ID
   * @param data - Password change data
   * @returns Success confirmation
   */
  async changeUserPassword(
    userId: string,
    data: {
      current_password: string
      new_password: string
    }
  ): Promise<{ success: boolean; message?: string }> {
    return this.patch(`users/${userId}/password`, data)
  }

  // ============= Tool Operations =============
  // NOTE: Tools are managed by governance-service at /v1/governance/tools

  /**
   * List tools with optional filters
   * @param params - Filter parameters
   * @returns Paginated tool list
   */
  async getTools(params?: {
    category?: 'calculation' | 'data_connector' | 'ai_pipeline'
    lifecycleStage?: 'draft' | 'generated'
    search?: string
    limit?: number
    offset?: number
  }): Promise<{
    tools: Array<{
      id: string
      name: string
      version: string
      category: 'calculation' | 'data_connector' | 'ai_pipeline'
      description?: string
      inputSchema: Record<string, unknown>
      outputSchema: Record<string, unknown>
      ownerMissionId?: string
      lifecycleStage: 'draft' | 'generated'
      compileStatus?: string
      sourcePaths?: string[]
      hasSource?: boolean
      availability?: {
        state?: string
        reason?: string
        canRepair?: boolean
      }
      governance: {
        requiredTrustLevel: string
      }
      metadata: {
        author: string
        tags: string[]
        createdAt: string
        updatedAt: string
        // Clone provenance (TOOL-COPIES.md task 2/fix 3-5): the lineage
        // link the picker and the Tools table use to group copies under
        // their original. Absent for an original or an import copy
        // (import doesn't record provenance yet).
        copyProvenance?: { clonedFromToolId?: string }
      }
    }>
    total: number
    limit: number
    offset: number
    hasNext: boolean
  }> {
    const queryParams: Record<string, string | number> = {}
    if (params?.category) queryParams['category'] = params.category
    if (params?.lifecycleStage) queryParams['lifecycle_stage'] = params.lifecycleStage
    if (params?.search) queryParams['search'] = params.search
    if (params?.limit) queryParams['limit'] = params.limit
    if (params?.offset) queryParams['offset'] = params.offset
    return this.get('governance/tools', queryParams)
  }

  /**
   * Get a single tool by ID
   * @param id - Tool ID
   * @returns Tool definition
   */
  async getTool(id: string): Promise<{
    id: string
    name: string
    version: string
    category: 'calculation' | 'data_connector' | 'ai_pipeline'
    nodes: unknown[]
    edges: unknown[]
    inputSchema: Record<string, unknown>
    outputSchema: Record<string, unknown>
    dataSources: unknown[]
    testCases: unknown[]
    metadata: Record<string, unknown>
    governance: Record<string, unknown>
    hostId?: string
  }> {
    return this.get(`governance/tools/${id}`)
  }

  /**
   * Update an existing tool
   * @param id - Tool ID
   * @param data - Tool update data
   * @returns Updated tool
   */
  async updateTool(id: string, data: {
    name?: string
    description?: string
    nodes?: unknown[]
    edges?: unknown[]
    inputSchema?: Record<string, unknown>
    outputSchema?: Record<string, unknown>
    dataSources?: unknown[]
    testCases?: unknown[]
    tags?: string[]
  }): Promise<{ id: string; name: string }> {
    return this.patch(`governance/tools/${id}`, data)
  }

  // ============= Cleanup =============

  destroy(): void {
    // Close all WebSocket connections
    for (const [, ws] of this.wsConnections) {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
    this.wsConnections.clear()
  }
}

// ============= Export Singleton Instance =============

export const unifiedApi = new UnifiedAPIClient()

// Re-export types for convenience
export type {
  Agent,
  AgentWithDetails,
  AgentRegistrationRequest,
  AgentDeploymentRequest,
  AgentTask,
  AgentMetrics,
  RuntimeHost,
  AgentListResponse,
  GovernanceReview,
  AgentLifecycleState,
  A2AMessage,
  Mission,
  MissionState,
  CreateMissionRequest,
  UpdateMissionRequest,
  MissionParticipant
}
